import { useEffect, useState } from "react";
import axios from '../context/axiosConfig';
import {
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
} from 'mdb-react-ui-kit';
import { FaTimes } from "react-icons/fa";
import PhenotypingMaintenance from "./phenotypingMaintenance";
import IrrigationMaintenance from "./irrigationMaintenance";
import ScoutingMaintenance from "./scoutingMaintenance";
import MeasurementMaintenance from "./measurementMaintenance";
import DestructionMaintenance from "./destructionMaintenance";
import HarvestMaintenance from "./harvestMaintenance";
import ManipulationMaintenance from "./manipulationMaintenance";
import ApplicationMaintenance from "./applicationMaintenance";
import DocumentationMaintenance from "./documentationMaintenance";

const categories = {
  "Irrigation": {
    "name": "Irrigation",
    "sublevel": ["Gift", "Drain", "Substrate"]
  },
  "Phenotyping": {
    "name": "Phenotyping",
    "sublevel": ["Sex", "Germination Speed", "Nutrient Imbalances", "Leaf Formation", "Plant Architecture", "Flower Architecture", "Flower Density", "Internode Spacing", "Flower Colour", "Trichome Expression", "Aroma", "Infection"]
  },
  "Scouting": {
    "name": "Scouting",
    "sublevel": ["Pests", "Diseases", "Mutation", "Nutrient Imbalance", "Environmental Stressor", "Other"]
  },
  "Measurement": {
    "name": "Measurement",
    "sublevel": ["Height", "Width", "Stem Diameter", "Node Count", "Branch Count", "LAI", "Planting Density", "Light Intensity", "Flower Thickness", "Weight"]
  },
  "Destruction": {
    "name": "Destruction",
    // "sublevel": ["Whole Plant", "Part of Plant", "Infected Flower", "Unrooted Clone", "Rooted Clone", "Mother Plant", "Back-up Plant", "Other"]
  },
  "Harvest/Post-Harvest": {
    "name": "Harvest/Post-Harvest",
    "sublevel": ["Harvest", "Post-Harvest", "Chemical Analysis"]
  },
  "Manipulation": {
    "name": "manipulation",
    "sublevel": ["Technique Applied"]
  },
  "Application & IPM": {
    "name": "Application & IPM",
    // "sublevel": ["Pesticide", "Fungicide", "Biostimulant", "Other"]
  },
  "Documentation/Other": {
    "name": "Documentation/Other",
    // "sublevel": ["Pictures", "Video", "Time-Lapse", "Macro Shots", "360 Shots", "Other"]
  },
}


const categoryKeys = Object.keys(categories);

/* ──────────────────────── component ─────────────────────────── */

export function MaintenanceSelector({
  selectedUid,
  setSelectedUid,
  repeatAction,
  type,
  selected_plant_id,
  setSelected_plant_id,
}) {
  /** navigation */
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSublevel, setSelectedSublevel] = useState(null);

  /** dropdown state for sub‑levels */
  const [expandedSubs, setExpandedSubs] = useState(new Set());

  /** form controls */
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedSev, setSelectedSev] = useState("");
  const [desc, setDesc] = useState("");
  const [isEditable, setIsEditable] = useState(false);

  /** modal */
  const [showRepeatModal, setShowRepeatModal] = useState(false);
  
  /** plot plants */
  const [plotPlantIds, setPlotPlantIds] = useState([]);
  const [loadingPlantIds, setLoadingPlantIds] = useState(false);
  
  const org_id = localStorage.getItem("organization");

  /* ——————————————— lifecycle helpers ——————————————— */

  /* reset when the component unmounts */
  useEffect(
    () => () => {
      setSelectedCategory(null);
      setSelectedSublevel(null);
    },
    []
  );
  
  /* Fetch all plant IDs for a plot when type="plot" */
  useEffect(() => {
    const fetchPlantIdsForPlot = async () => {
      if (type === "plot" && selectedUid) {
        setLoadingPlantIds(true);
        try {
          // Get all plants in the organization
          const res = await axios.get(`orgs/${org_id}/plants/`, { withCredentials: true });
          
          // Filter plants that belong to the selected plot
          const plotPlants = res.data.filter(plant => plant.plot_id === selectedUid);
          
          // Extract plant IDs
          const plantIds = plotPlants.map(plant => plant.plant_id);
          
          // Set the plant IDs in state
          setPlotPlantIds(plantIds);
          
          // Set the selected_plant_id to the array of plant IDs if it's a setter function
          if (setSelected_plant_id) {
            setSelected_plant_id(plantIds);
          }
          
          console.log(`Found ${plantIds.length} plants in plot ${selectedUid}`);
        } catch (err) {
          console.error("Error fetching plant IDs for plot:", err);
        } finally {
          setLoadingPlantIds(false);
        }
      }
    };
    
    fetchPlantIdsForPlot();
  }, [type, selectedUid, org_id, setSelected_plant_id]);

  /* ——————————————— handlers ——————————————— */

  /** choose / leave a top‑level category */
  const handleCategoryClick = (key) => {
    setSelectedCategory(key);
    setExpandedSubs(new Set()); // reset any open subs
  };
  const handleCategoryBackClick = () => {
    setSelectedCategory("");
    setExpandedSubs(new Set());
  };

  /** open / close one sub‑level */
  const handleSublevelToggle = (sub) => {
    setExpandedSubs((prev) => {
      const next = new Set(prev);
      next.has(sub) ? next.delete(sub) : next.add(sub);
      return next;
    });
  };

  /** pick a sub‑level and move on to the form */
  const handleSublevelClick = (sublevel, data = null) => {
    // when Phenotyping returns `data`, stash it somewhere or push to form
    if (data) {
      // do whatever your old submit logic expected; e.g. merge into desc:
      setDesc(JSON.stringify(data));          // example
      }
      setSelectedSublevel(sublevel);
    };
  const handleSublevelBackClick = () => setSelectedSublevel(null);

  /** simple input handlers */
  const handleOptionsChange = (e) => setSelectedOption(e.target.value);
  const handleOptionsSevChange = (e) => setSelectedSev(parseInt(e.target.value));
  const handleDescChange = (e) => setDesc(e.target.value);

  /** modal visibility */
  const toggleRepeatShow = () => setShowRepeatModal((s) => !s);

  /** submit */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      category: selectedCategory,
      performance: selectedSublevel,
      uid: selectedUid,
      description: desc,
      alarmlevel: selectedOption,
      severity: parseInt(selectedSev, 10),
    };

    try {
      const res = await axios.post(`maintenance/${type}/${selectedUid}`, data);
      if (res?.status === 200) console.log(res.data);
    } catch (err) {
      console.error("Error occurred:", err);
    }

    setShowRepeatModal(true);
  };

  /** repeat‑action modal buttons */
  const handleRepeatAction = (repeat) => {
    repeatAction(repeat);
    if (repeat) {
      setSelectedUid("");
      setIsEditable(true);
    } else {
      setIsEditable(false);
    }
    setShowRepeatModal(false);
  };

  /* ─────────────────────────── render ────────────────────────── */
  return (
    <>
      {/* ───────── first screen: choose category ───────── */}
      {!selectedCategory && (
        <div className="maintenaceGrid" style={{marginBottom: "20px"}}>
          {categoryKeys.map((key) => (
            <div
              key={key}
              className="purpleBtn extraGridBtn"
              onClick={() => handleCategoryClick(key)}
            >
              {key}
            </div>
          ))}
        </div>
      )}

    {/* ───────── second screen: sub‑level dropdowns ───────── */}
    {selectedCategory === "Phenotyping" && !selectedSublevel && (
      <PhenotypingMaintenance
        onBack={() => { setSelectedCategory(""); setExpandedSubs(new Set()); }}
        onSelect={data => handleSublevelClick("Phenotyping-Done", data)}
        selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
      />
    )}

  {selectedCategory === "Irrigation" && !selectedSublevel && (
        <IrrigationMaintenance
          onBack={() => { setSelectedCategory(""); setExpandedSubs(new Set()); }}
          onSelect={data => handleSublevelClick("Irrigation-Done", data)}
          selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
        />
      )}

  {selectedCategory === "Scouting" && !selectedSublevel && (
        <ScoutingMaintenance
           onBack={() => { setSelectedCategory(""); setExpandedSubs(new Set()); }}
                onSelect={data => handleSublevelClick("Scouting-Done", data)}
                selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
              />
              )}

            {selectedCategory === "Measurement" && !selectedSublevel && (
              <MeasurementMaintenance
                onBack={() => { setSelectedCategory(""); setExpandedSubs(new Set()); }}
                onSelect={data => handleSublevelClick("Measurement-Done", data)}
                selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
              />
              )}

              {selectedCategory === "Destruction" && !selectedSublevel && (
                        <DestructionMaintenance
                        onBack={() => {setSelectedCategory(""); setExpandedSubs(new Set()); }}
                        onSelect={data => handleSublevelClick("Destruction-Done", data)}
                        selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
                        />
                        )}

                        {selectedCategory === "Harvest/Post-Harvest" && !selectedSublevel && (
                        <HarvestMaintenance
                        onBack={() => { setSelectedCategory(""); setExpandedSubs(new Set()); }}
                        plantId={selectedUid}
                        selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
                        onSelect={data => handleSublevelClick("Harvest/Post-Harvest-Done", data)}
                        />
                        )}

                        {selectedCategory === "Manipulation" && !selectedSublevel && (
                        <ManipulationMaintenance
                        onBack={() => { setSelectedCategory(""); setExpandedSubs(new Set()); }}
                        plantId={selectedUid}
                        selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
                        onSelect={data => handleSublevelClick("Manipulation-Done", data)}
                        />
                        )}

                        {selectedCategory === "Application & IPM" && !selectedSublevel && (
                        <ApplicationMaintenance
                        onBack={() => { setSelectedCategory(""); setExpandedSubs(new Set()); }}
                        plantId={selectedUid}
                        selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
                        onSelect={data => handleSublevelClick("Application & IPM-Done", data)}
                        />
                        )}

                        {selectedCategory === "Documentation/Other" && !selectedSublevel && (
                        <DocumentationMaintenance
                        onBack={() => { setSelectedCategory(""); setExpandedSubs(new Set()); }}
                        plantId={selectedUid}
                        selected_plant_id={type === "plot" ? plotPlantIds : selected_plant_id}
                        onSelect={data => handleSublevelClick("Documentation-Done", data)}
                        />
                        )}

                      {selectedCategory && 
                       selectedCategory !== "Phenotyping" && 
                       selectedCategory !== "Irrigation" && 
                       selectedCategory !== "Scouting" && 
                       selectedCategory !== "Measurement" && 
                       selectedCategory !== "Destruction" && 
                       selectedCategory !== "Harvest/Post-Harvest" && 
                       selectedCategory !== "Manipulation" && 
                       selectedCategory !== "Application & IPM" &&
                       selectedCategory !== "Documentation/Other" && 
                       !selectedSublevel && (
                      <>
                        <div className="maintenanceHeader">
                        <h3>{categories[selectedCategory].name}</h3>
                        <button className="uploadMaintenanceBtn">Upload</button>
                        </div>

                        <div className="maintenaceGrid maintenanceList">
                        {/* back to category list */}
              <div
                className="purpleBtn extraGridBtn blueBtn"
                onClick={handleCategoryBackClick}
              >
                Back
              </div>

              {/* one toggleable card for every sub-level ----------------------- */}
              {categories[selectedCategory].sublevel?.map((sub) => {
                const open = expandedSubs.has(sub);
                return (
                <div key={sub} className="dropdownBlock">
                  {/* header row that opens/closes the sub-panel */}
                  <div
                  className="purpleBtn extraGridBtn"
                  onClick={() => handleSublevelToggle(sub)}
                  >
                  {sub} {open ? "▲" : "▼"}
                  </div>

                  {/* inner panel: just a "Select" button for these categories */}
                  {open && (
                  <div className="dropdownInner">
                    <button
                    className="purpleBtn extraGridBtn greenBtn"
                    onClick={() => handleSublevelClick(sub)}
                    >
                    Select
                    </button>
                  </div>
                  )}
                </div>
                );
              })}
              </div>
            </>
            )}

              {/* ───────── third screen: the form ───────── */}
      {selectedSublevel && (
        <>
          <div
            className="purpleBtn extraGridBtn blueBtn"
            onClick={handleSublevelBackClick}
          >
            Back
          </div>

          <h3>
            Selected Top level maintenance: {selectedCategory}
            <br />
            Selected Sub level maintenance: {selectedSublevel}
          </h3>

          <div className="editFormDiv">
            <form className="editForm" onSubmit={handleSubmit}>
              {/* UID */}
              <div className="maintenanceFormElement">
                <label htmlFor="uid">UID</label>
                <input
                  type="text"
                  id="uid"
                  className="maintenanceInput"
                  value={selectedUid}
                  readOnly={!isEditable}
                  onChange={(e) => isEditable && setSelectedUid(e.target.value)}
                />
              </div>

              {/* Description */}
              <div className="descFlex">
                <label htmlFor="desc">Description</label>
                <textarea
                  id="desc"
                  className="maintenanceInput flexTextBox"
                  rows="6"
                  onChange={handleDescChange}
                />
              </div>

              {/* Alarm level */}
              <div className="maintenanceFormElement">
                <label htmlFor="alarmLvl">Alarm Level</label>
                <select
                  id="alarmLvl"
                  className="maintenanceInput"
                  value={selectedOption}
                  onChange={handleOptionsChange}
                >
                  <option value="">Select...</option>
                  <option value="0">Low Alert</option>
                  <option value="1">Medium Alert</option>
                  <option value="2">High Alert</option>
                </select>
              </div>

              {/* Severity */}
              <div className="maintenanceFormElement">
                <label htmlFor="sevLvl">Severity Level</label>
                <select
                  id="sevLvl"
                  className="maintenanceInput"
                  value={selectedSev}
                  onChange={handleOptionsSevChange}
                >
                  <option value="">Select...</option>
                  {[...Array(10)].map((_, i) => (
                    <option key={i} value={i}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              <input
                type="submit"
                value="Submit"
                className="maintenanceFormSubmit"
                style={{ marginTop: 30 }}
              />
            </form>
          </div>
        </>
      )}

      {/* ───────── repeat‑action modal ───────── */}
      {showRepeatModal && (
        <MDBModal show={showRepeatModal} setShow={setShowRepeatModal} tabIndex="-1">
          <MDBModalDialog>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <button className="close-btn" onClick={toggleRepeatShow}>
                    <FaTimes />
                  </button>
                  <h1 className="title">
                    Repeat this action for another {type}?
                  </h1>
                </MDBModalTitle>
              </MDBModalHeader>

              <MDBModalBody>
                <div className="centerFlex">
                  <input
                    type="button"
                    value="Yes"
                    className="maintenanceFormSubmit longBtn longBtnTopMargin"
                    onClick={() => handleRepeatAction(true)}
                  />
                  <br />
                  <input
                    type="button"
                    value="No"
                    className="maintenanceFormSubmit longBtn"
                    onClick={() => handleRepeatAction(false)}
                  />
                </div>
              </MDBModalBody>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      )}
    </>
  );
}